import { computed, unref } from 'vue'
import { ymd } from '@/utils/agendaItem'

/**
 * 월 그리드 계산 + 레인 패킹.
 *
 * ── 두 레인 그룹으로 나누는 이유 (이게 이 파일의 핵심) ──
 *
 * 기존 MonthView(packEventsForWeek)와 TaskMonthView(packed)는 정렬 키만 다른
 * 같은 greedy 구간 패킹이었다. 문제는 **모든 항목을 한 줄 세우기에 넣었다**는 것이다.
 *
 * 그러면 5일짜리 휴가와 그날 09:00 회의가 같은 행을 두고 경쟁한다. 휴가가
 * 1~3행을 먹으면 회의는 4행으로 밀리고, 모바일의 3레인 컷에 걸려 사라진다.
 * 하루에 일정이 2건뿐인 날인데도 그렇다.
 *
 * 그래서 입력을 먼저 둘로 나눈다.
 *   그룹 A(스팬)  : 종일이거나 여러 날 걸침 → 컬럼 범위를 점유하므로 구간 패킹
 *   그룹 B(포인트): 단일일 시간 항목      → 컬럼별 세로 스택 (서로 경쟁 안 함)
 *
 * A 가 레인 1..k 를 쓰고 B 는 컬럼별로 k+1.. 부터 독립적으로 쌓인다.
 * FullCalendar 와 구글 캘린더가 둘 다 쓰는 방식이고, "그리드가 어수선하다"와
 * "읽힌다"의 차이가 여기서 갈린다.
 */

const WEEK_DAYS = ['일', '월', '화', '수', '목', '금', '토']

// 스팬 정렬 우선순위: 부재/커버 유형이 위로 와야 한 눈에 읽힌다
const EVENT_TYPE_PRIORITY = {
  vacation: 0,
  dayoff: 1,
  substitute: 2,
  fieldwork: 3,
  inspection: 4,
  work: 5
}

/** 해당 월을 6주(42일) 그리드로 펼친다 */
export function buildMonthWeeks(monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
  const start = new Date(first)
  start.setDate(first.getDate() - first.getDay()) // 그 주 일요일부터

  const weeks = []
  const cursor = new Date(start)
  for (let w = 0; w < 6; w += 1) {
    const days = []
    for (let d = 0; d < 7; d += 1) {
      days.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    weeks.push(days)
  }
  return weeks
}

/** 월 그리드가 덮는 전체 범위 (아젠다 조회용) */
export function monthGridRange(monthDate) {
  const weeks = buildMonthWeeks(monthDate)
  return { from: ymd(weeks[0][0]), to: ymd(weeks[5][6]) }
}

function isSpanItem(item) {
  return item.allDay || (item.endDate && item.endDate !== item.date)
}

/**
 * 레인 배치 순서.
 *
 * **길이를 우선하지 않는다.** 예전에는 긴 스팬을 맨 위로 올렸는데, 그러면
 * 3일짜리 업무가 하루짜리 휴가보다 위에 오면서 "누가 언제 빠지나" 가
 * 한눈에 안 읽혔다. 달력에서 먼저 보여야 하는 건 길이가 아니라 종류다.
 *
 * 순서: 일정 > 업무, 그 안에서 휴가 > 휴무 > 대직 > 외근 > 점검 > 업무.
 * 길이는 같은 유형끼리의 tie-break 로만 쓴다(긴 것이 위).
 */
function spanSortKey(item) {
  const kind = item.kind === 'schedule' ? 0 : 1
  const type = item.kind === 'schedule' ? (EVENT_TYPE_PRIORITY[item.eventType] ?? 9) : 0
  const len = -(item.spanDays || 1)
  return [kind, type, len, item.startAt || '', item.uid]
}

/** 포인트는 컬럼별 세로 스택이라 길이 개념이 없다. 종류 > 유형 > 시각. */
function pointSortKey(item) {
  const kind = item.kind === 'schedule' ? 0 : 1
  const type = item.kind === 'schedule' ? (EVENT_TYPE_PRIORITY[item.eventType] ?? 9) : 0
  return [kind, type, item.startAt || '', item.uid]
}

function cmpKey(a, b) {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] < b[i]) return -1
    if (a[i] > b[i]) return 1
  }
  return 0
}

/**
 * 한 주(7일)에 대한 레인 배치.
 * @returns {{ spans: Array, points: Array<Array>, laneCount: number }}
 *   spans  : [{ item, lane, colStart(0-6), colEnd(0-6), continuesLeft, continuesRight }]
 *   points : 컬럼별 배열. points[col] = [item, ...] (시간순)
 */
export function packWeek(weekDays, items) {
  const weekStart = ymd(weekDays[0])
  const weekEnd = ymd(weekDays[6])

  const inWeek = items.filter(
    (it) => it.date && it.date <= weekEnd && (it.endDate || it.date) >= weekStart
  )

  // ── 그룹 A: 스팬 (구간 패킹) ──
  const spanItems = inWeek.filter(isSpanItem)
  spanItems.sort((a, b) => cmpKey(spanSortKey(a), spanSortKey(b)))

  const lanes = [] // lanes[i] = 해당 레인에서 점유된 컬럼 Set
  const spans = []

  for (const item of spanItems) {
    const itemEnd = item.endDate || item.date
    const colStart = Math.max(0, weekDays.findIndex((d) => ymd(d) === item.date))
    let colEnd = weekDays.findIndex((d) => ymd(d) === itemEnd)
    if (colEnd === -1) colEnd = 6
    const s = item.date < weekStart ? 0 : colStart
    const e = itemEnd > weekEnd ? 6 : colEnd

    let lane = 0
    for (;;) {
      if (!lanes[lane]) lanes[lane] = new Set()
      let free = true
      for (let c = s; c <= e; c += 1) {
        if (lanes[lane].has(c)) { free = false; break }
      }
      if (free) break
      lane += 1
    }
    for (let c = s; c <= e; c += 1) lanes[lane].add(c)

    spans.push({
      item,
      lane,
      colStart: s,
      colEnd: e,
      continuesLeft: item.date < weekStart,
      continuesRight: itemEnd > weekEnd
    })
  }

  // ── 그룹 B: 포인트 (컬럼별 세로 스택) ──
  const points = weekDays.map((d) => {
    const key = ymd(d)
    return inWeek
      .filter((it) => !isSpanItem(it) && it.date === key)
      // 스팬과 같은 규칙을 적용한다. 시각순만으로 쌓으면 09:00 업무가
      // 10:00 외근 위로 올라와 스팬 영역과 순서 규칙이 어긋난다.
      .sort((a, b) => cmpKey(pointSortKey(a), pointSortKey(b)))
  })

  return { spans, points, laneCount: lanes.length }
}

/**
 * @param {Ref<Date>} monthDateRef
 * @param {Ref<Array>} itemsRef  정규화된 AgendaItem 배열
 */
export function useMonthGrid(monthDateRef, itemsRef) {
  const weeks = computed(() => buildMonthWeeks(unref(monthDateRef)))
  const range = computed(() => monthGridRange(unref(monthDateRef)))
  const packedWeeks = computed(() =>
    weeks.value.map((days) => ({ days, ...packWeek(days, unref(itemsRef) || []) }))
  )
  return { WEEK_DAYS, weeks, range, packedWeeks }
}

export { WEEK_DAYS, EVENT_TYPE_PRIORITY }
